/**
 * The client's own copy of the crash-point derivation.
 *
 * It exists so that a player's browser can check a round without taking the
 * server's word for it. The fairness endpoint answers with `verified: true`,
 * which is worth very little on its own: it is the server confirming its own
 * arithmetic. What makes the scheme a proof is that anyone can redo it, and this
 * is the game doing that on the player's behalf.
 *
 * It mirrors server/internal/fair/fair.go and the reference implementation in
 * docs/PROVABLY_FAIR.md. If any of the three changes, all three change.
 *
 *   mac       = HMAC-SHA256(key = seed, msg = decimal ASCII of nonce)
 *   h         = top 44 bits of mac, big-endian
 *   crashX100 = floor(100 * rtp.num * 2^44 / (rtp.den * h)), clamped to [100, 10000000]
 *
 * The RTP is an *input*, not a constant in this file. It is settable from the
 * internal back office, so each round carries the fraction it was sealed with and
 * that is what a verification has to use: checking an old round with today's value
 * would declare an honest round broken. Every result the server publishes carries
 * `rtp`, and a missing one means the default -- which is what every round before
 * the setting existed used.
 *
 * BigInt throughout: 2^44 exceeds the exact integer range of a double, and a
 * verification that is off by one in the last hundredth is not a verification.
 */

/** The default: 100/101, or 99.0099%. */
export const DEFAULT_RTP = { num: 100, den: 101 } as const;

const MIN_X100 = 100n;
const MAX_X100 = 10_000_000n;

interface RTPish {
  num: number;
  den: number;
}

/**
 * The fraction to derive with, having decided what a missing or nonsensical value
 * means.
 *
 * The bounds mirror `fair.Valid` on the server. A value outside them cannot have
 * produced a round this game played, so falling back to the default is both safe
 * and the only answer that can be right -- and it keeps a malformed field in a
 * response from turning into a verification failure the player would read as
 * cheating.
 */
function usableRTP(rtp?: RTPish | null): { num: bigint; den: bigint } {
  const num = rtp?.num ?? 0;
  const den = rtp?.den ?? 0;
  const valid =
    Number.isSafeInteger(num) &&
    Number.isSafeInteger(den) &&
    num > 0 &&
    den > 0 &&
    num <= den &&
    den <= 1_000_000 &&
    num * 100 >= den * 80;
  if (!valid) return { num: BigInt(DEFAULT_RTP.num), den: BigInt(DEFAULT_RTP.den) };
  return { num: BigInt(num), den: BigInt(den) };
}

/**
 * Parses lowercase or uppercase hex into bytes. Throws on anything else.
 *
 * The buffer is allocated explicitly rather than by `new Uint8Array(n)` so the
 * result is typed over an ArrayBuffer: WebCrypto will not take a view that might
 * sit on a SharedArrayBuffer.
 */
function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.trim();
  if (clean.length === 0 || clean.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(clean)) {
    throw new Error('fair: not a hex string');
  }
  const out = new Uint8Array(new ArrayBuffer(clean.length / 2));
  for (let i = 0; i < out.length; i += 1) {
    out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/** SHA-256 of the *bytes* the hex string denotes, which is what the commitment is. */
export async function commitmentOf(seedHex: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', hexToBytes(seedHex));
  return bytesToHex(new Uint8Array(digest));
}

/**
 * The crash point a seed, a round number and an RTP produce, in hundredths.
 *
 * The RTP is optional so that a caller checking a round from a screenshot -- which
 * has a seed, a commitment and a multiplier on it and no fraction -- still gets an
 * answer.
 */
export async function crashX100(
  seedHex: string,
  nonce: number,
  rtp?: RTPish | null,
): Promise<number> {
  const key = await crypto.subtle.importKey(
    'raw',
    hexToBytes(seedHex),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const mac = new Uint8Array(
    await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(String(nonce))),
  );

  let v = 0n;
  for (let i = 0; i < 8; i += 1) v = (v << 8n) | BigInt(mac[i] ?? 0);
  const h = v >> 20n; // the top 44 bits
  if (h === 0n) return Number(MAX_X100);

  const r = usableRTP(rtp);
  const x100 = (100n * r.num * (1n << 44n)) / (r.den * h);
  return Number(x100 < MIN_X100 ? MIN_X100 : x100 > MAX_X100 ? MAX_X100 : x100);
}

export interface FairCheck {
  /** SHA-256(seed) equals the commitment published before betting opened. */
  commitOk: boolean;
  /** The seed derives exactly the multiplier the round paid out at. */
  crashOk: boolean;
  /** What this browser derived, for showing beside what the server reported. */
  derivedX100: number;
}

/**
 * Checks one round end to end, in this browser.
 *
 * Both halves matter and they fail differently: a commitment mismatch means the
 * seed was swapped after the fact, a crash mismatch means the seed is real but
 * the result served with it is not.
 */
export async function checkRound(
  seedHex: string,
  commitHash: string,
  nonce: number,
  reportedX100: number,
  rtp?: RTPish | null,
): Promise<FairCheck> {
  const commit = await commitmentOf(seedHex);
  const derivedX100 = await crashX100(seedHex, nonce, rtp);
  return {
    commitOk: commit.toLowerCase() === commitHash.trim().toLowerCase(),
    crashOk: derivedX100 === reportedX100,
    derivedX100,
  };
}

/**
 * The RTP as a percentage string with four decimals, without going through a
 * float.
 *
 * Four places because that is what the game rules publish and what the back
 * office accepts: 99.01% and 99.0099% are two different figures. Integer
 * arithmetic for the same reason the derivation uses BigInt -- a number the player
 * is being told the game pays should not have arrived via a rounding error.
 */
export function rtpPercent(rtp?: RTPish | null): string {
  const r = usableRTP(rtp);
  const scaled = (r.num * 1_000_000n) / r.den;
  const whole = scaled / 10_000n;
  const frac = (scaled % 10_000n).toString().padStart(4, '0');
  return `${whole}.${frac}`;
}
